import styled from "styled-components";
import axios from "utils/axios";
import configFile from "config.json";
import { useCallback, useEffect, useMemo, useState } from "react";

const API_BASE = `${configFile.PROD_SERVER_URL}/menu2`;
// TODO: getComboGroupDefinitions
const ConfigTab = ({ deal = {} }) => {
  const [selectedStepData, setSelectedStepData] = useState({});
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const groups = useMemo(() => deal.groups || [], [deal]);

  const fetchGroupData = useCallback(async (groupId) => {
    try {
      if (!groupId) {
        setSelectedStepData({});
        return;
      }
      const { data: result } = await axios.post(`${API_BASE}/combo/getComboGroupMenus`, { groupId })
      setSelectedStepData(result?.data || {})
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    if (groups.length === 0) {
      setSelectedGroupId(null);
      setSelectedStepData({});
      return;
    }
    const isSelectedValid = groups.some((group) => group.id === selectedGroupId);
    if (!isSelectedValid) {
      setSelectedGroupId(groups[0]?.id ?? null);
    }
  }, [groups, selectedGroupId]);

  useEffect(() => {
    if (selectedGroupId) {
      fetchGroupData(selectedGroupId);
    }
  }, [fetchGroupData, selectedGroupId]);

  const groupItems = useMemo(
    () => Object.values(selectedStepData || {}),
    [selectedStepData]
  );
  return (
    <Wrap>
      <TopRow>
        <StepRow>
          {
            groups.map((group) => (
              <StepCard
                key={group.id}
                $active={group.active || selectedGroupId === group.id}
                onClick={() => {
                  setSelectedGroupId(group.id);
                }}
              >
                <StepBadge>{group.name}</StepBadge>
                <span>{group.groupType}</span>
              </StepCard>
            ))
          }
        </StepRow>
      </TopRow>
      {
        selectedGroupId && (
          <Results>
            <ResultsTitle>Сонгогдсон бараанууд</ResultsTitle>
            {groupItems.length === 0 ? (
              <EmptyState>Сонгосон бараа байхгүй байна.</EmptyState>
            ) : (
              <ResultsGrid>
                {groupItems.map((item) => (
                  <ResultCard key={item.id}>
                    <ResultHeader>
                      <ResultName>{item.name}</ResultName>
                      <ResultType>{item.type}</ResultType>
                    </ResultHeader>
                    {item.description && <ResultDesc>{item.description}</ResultDesc>}
                    {item.definitions?.length > 0 && (
                      <DefList>
                        {item.definitions.map((def) => (
                          <DefItem key={def.id}>
                            <span>{def.masterName}</span>
                            <DefMeta>
                              <span>MI: {def.miMasterObjNum || "-"}</span>
                              <span>Price: {def.price ?? "-"}</span>
                            </DefMeta>
                          </DefItem>
                        ))}
                      </DefList>
                    )}
                  </ResultCard>
                ))}
              </ResultsGrid>
            )}
          </Results>
        )
      }
    </Wrap>
  )
};

const Wrap = styled.div`
  display: grid;
  gap: 18px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  overflow-x: auto;
  scrollbar-width: auto;
`;

const StepRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding-bottom: 4px;
  max-width: 100%;
  scrollbar-gutter: stable;
`;

const StepCard = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid ${({ $active, $ghost }) => ($active ? "rgba(22, 119, 255, 0.6)" : $ghost ? "rgba(22, 119, 255, 0.25)" : "rgba(148, 163, 184, 0.3)")};
  background: ${({ $active, $ghost }) => ($active ? "rgba(22, 119, 255, 0.14)" : $ghost ? "rgba(22, 119, 255, 0.08)" : "rgba(255, 255, 255, 0.92)")};
  color: #1a1f2b;
  font-weight: 600;
  min-width: 160px;
  justify-content: space-between;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  &:hover {
    border-color: rgba(22, 119, 255, 0.65);
    background: rgba(22, 119, 255, 0.12);
    transform: translateY(-1px);
  }
`;

const StepBadge = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 2px solid rgba(22, 119, 255, 0.4);
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #1677ff;
  background: rgba(255, 255, 255, 0.9);
`;

const Results = styled.div`
  display: grid;
  gap: 12px;
`;

const ResultsTitle = styled.div`
  font-weight: 700;
  color: #1a1f2b;
  letter-spacing: 0.02em;
`;

const EmptyState = styled.div`
  padding: 12px 14px;
  border-radius: 6px;
  border: 1px solid rgba(22, 119, 255, 0.18);
  background: rgba(22, 119, 255, 0.08);
  color: #5b6b7b;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
`;

const ResultCard = styled.div`
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 6px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.96);
  display: grid;
  gap: 8px;
  box-shadow: 0 10px 24px rgba(22, 119, 255, 0.08);
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;

const ResultName = styled.div`
  font-weight: 700;
  color: #1a1f2b;
`;

const ResultType = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #1677ff;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const ResultDesc = styled.div`
  font-size: 13px;
  color: #546174;
`;

const DefList = styled.div`
  display: grid;
  gap: 6px;
`;

const DefItem = styled.div`
  display: grid;
  gap: 2px;
  font-size: 13px;
  color: #1a1f2b;
`;

const DefMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  font-size: 12px;
  color: #6b7b8b;
`;

export default ConfigTab;
